import { AxiosError } from 'axios';
import { SignInCredentials, ValidateCredentials, RegisterCredentials } from '../../models';
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { fetchUser, registerUser, signInRequest, validateUser } from '../../services/auth.service';
import { updateUserStatus } from '../../services/profile.service';
import { User } from '../../types/user';
import { clearStorage } from '../../utils/storage';

import { api } from '../../services/api';
import { setAuthorizationHeader } from '../../services/interceptors';
import { createTokenCookies, getToken, removeTokenCookies } from '../../utils/tokenCookies';
import { RuleEntity } from 'types/rule';
import { Tutorial } from 'types/tutorial';
import GeneralItens from 'services/general.service';
import { JSX } from 'react/jsx-runtime';
import { Step } from '../../types/steps';
import { fetchSteps } from 'services/steps.service';
import { clearRegistrationUser } from 'utils/fns';
import { getNotifications } from 'services/notification.service';
import { useNavigate } from 'react-router';
import { StageIds } from 'utils/constants';

interface AuthContextData {
    login: (credentials: SignInCredentials) => Promise<void | AxiosError>;
    loginWithToken: (access_token: string) => Promise<void>;
    signOut: (pathname?: string) => void;
    updateNotifications: () => void;
    rule?: RuleEntity;
    refreshUser: () => void;
    onValidateUser: (credentials: ValidateCredentials) => Promise<void | AxiosError>;
    onRegisterUser: (credentials: RegisterCredentials) => Promise<void | AxiosError>;
    user?: User;
    isAuthenticated: boolean;
    loadingUserData: boolean;
    setRule: (val?: any) => void;
    tutorial?: Tutorial;
    setTutorial: (val?: any) => void;
    dates?: [];
    setDates: (val?: any) => void;
    appCurrentStep: any;
    setAppCurrentStep: (val?: any) => void;
    notifications: any[];
    setNotifications: (val?: any) => void;
    currentStep?: Step;
    showNotifcation: boolean;
    setShowNotification: (val?: any) => void;
    isBossUser?: boolean;
}

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthContext = createContext({} as AuthContextData);

function AuthProvider({ children }: AuthProviderProps) {
    const navigate = useNavigate();
    const token = getToken();
    const isAuthenticated = Boolean(token);

    const [rule, setRule] = useState<RuleEntity>();
    const [user, setUser] = useState<User | undefined>();
    const [loadingUserData, setLoadingUserData] = useState(true);
    const [tutorial, setTutorial] = useState<Tutorial>();
    const [dates, setDates] = useState<any>([]);
    const [appCurrentStep, setAppCurrentStep] = useState('');
    const [notifications, setNotifications] = useState<any>([]);
    const [currentStep, setCurrentStep] = useState<Step | undefined>();
    const [stepsList, setStepsList] = useState<Step[]>([]);
    const [notificationsLoaded, setNotificationsLoaded] = useState(false);
    const [showNotifcation, setShowNotification] = useState(false);
    const [isBossUser, setIsBossUser] = useState(false);

    useEffect(() => {
        const checkCurrentStep = (steps: Step[]) => {
            const now = Date.now();
            const current = steps.find((step) => now >= step.startDate && now <= step.endDate);

            console.log('current_step_ctx', current);
            setCurrentStep(current);
        };

        if (stepsList.length) {
            checkCurrentStep(stepsList);
        }
    }, [stepsList]);

    async function getSteps() {
        const response = await fetchSteps();
        setStepsList(response);
    }

    const signOut = useCallback((pathname?: string) => {
        removeTokenCookies();
        clearStorage();
        setAuthorizationHeader(api.defaults, '');
        setUser(undefined);
        setLoadingUserData(false);
        navigate(pathname || '/login');
    }, []);

    async function updateNotifications() {
        if (!user || !user._id || notificationsLoaded) return;
        if (currentStep?._id === StageIds.STAGE_INSCRICAO) {
            const data = await getNotifications(clearRegistrationUser(user._id));
            setNotifications(data);

            if (data) {
                setShowNotification(true);
            }
        }
    }

    async function fetchUserDetail() {
        if (!!user) {
            const { dates, rules, tutorial } = await GeneralItens();
            await getSteps();

            setRule(rules);
            setTutorial(tutorial);
            setDates(dates);
        }
    }

    useEffect(() => {
        const checkCurrentStep = (steps: Step[]) => {
            const now = Date.now();
            const current = steps.find((step) => now >= step.startDate && now <= step.endDate);

            console.log('current_step', current);
            setCurrentStep(current);
        };

        if (stepsList.length) {
            checkCurrentStep(stepsList);
        }
    }, [stepsList]);

    async function login({ username, password }: SignInCredentials) {
        try {
            const auth = await signInRequest({ username, password });

            if (auth.message) {
                throw auth;
            }

            const { access_token, refreshToken, permissions, roles } = auth;

            createTokenCookies(access_token, refreshToken);
            setAuthorizationHeader(api.defaults, access_token);

            const data = await fetchUser();

            const userData = { _id: data._id, ...data, permissions, roles };
            setUser(userData);
            await updateNotifications();
            return userData;
        } catch (error) {
            const err = error as AxiosError;
            throw err;
        }
    }

    async function loginWithToken(access_token: string) {
        try {
            if (!access_token) {
                return;
            }

            createTokenCookies(access_token, '');
            setAuthorizationHeader(api.defaults, access_token);

            const data = await fetchUser();

            const { name, image, extra } = data;

            const userData = {
                _id: data._id,
                ...data,
                extra,
                name,
                image,
                permissions: null,
                roles: null
            };

            setUser(userData);
            await updateNotifications();

            return userData;
        } catch (error) {
            const err = error as AxiosError;
            throw err;
        }
    }

    async function refreshUser() {
        const updatedUser = await updateUserStatus();
        setUser(updatedUser);
    }

    async function onValidateUser(data: ValidateCredentials) {
        const result = await validateUser(data);
        return result;
    }

    async function onRegisterUser(data: RegisterCredentials) {
        const result = await registerUser(data);
        if (result.status === 'OK') {
            setTimeout(async () => {
                await login({ username: result._id, password: data.password });
            }, 1e3);
        } else {
            throw result;
        }
    }

    useEffect(() => {
        const interval = setInterval(() => {
            updateNotifications();
        }, 300000);

        return () => clearInterval(interval);
    }, [updateNotifications]);

    useEffect(() => {
        if (user && user._id) {
            fetchUserDetail();
        }
    }, [user]);

    useEffect(() => {
        const token = getToken();

        async function getUserData() {
            setLoadingUserData(true);

            try {
                fetchUser()
                    .then(async (resp) => {
                        if (resp) {
                            const { _id, name, image, email, permissions, roles, detail } = resp;
                            setUser({ ...resp, _id, name, image, email, permissions, roles, detail });
                        }
                    })
                    .finally(() => {
                        updateNotifications();
                    });
            } catch (error) {
                signOut();
            } finally {
            }

            setLoadingUserData(false);
        }

        if (!user && token) {
            setAuthorizationHeader(api.defaults, token);
            getUserData();
        }

        async function updateNotificationsPeriodically() {
            if (user && user._id) {
                console.log('Chamando fetchNotifications...');
                await updateNotifications();
            } else {
                console.log('Usuário não definido, não chamando fetchNotifications.');
            }
        }

        const intervalId = setInterval(updateNotificationsPeriodically, 300000);

        // Chama uma vez ao montar
        updateNotificationsPeriodically();

        function handleSignoutEvent() {
            signOut();
        }
        document.addEventListener('signout', handleSignoutEvent, false);
        return () => {
            document.removeEventListener('signout', handleSignoutEvent);
        };
    }, [user, signOut]);

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                user,
                loadingUserData,
                login,
                rule,
                setRule,
                loginWithToken,
                signOut,
                updateNotifications,
                refreshUser,
                onValidateUser,
                onRegisterUser,
                tutorial,
                setTutorial,
                dates,
                setDates,
                currentStep,
                appCurrentStep,
                setAppCurrentStep,
                notifications,
                setNotifications,
                showNotifcation,
                setShowNotification
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export { AuthProvider, useAuth };
